import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "./services/supabase";
import Header from "./components/Header";
import { LoadingIndicator } from "./components/LoadingIndicator";

export default function Return() {
  const { connectedAccountId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkAccountStatus = async () => {
      if (!connectedAccountId) {
        setError("Missing account ID");
        setLoading(false);
        return;
      }

      try {
        console.log("Checking status for account:", connectedAccountId);
        
        // First, try to get account status from Stripe
        try {
          const response = await fetch('/check-account-type', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              account_id: connectedAccountId
            }),
          });
          
          // Check if the response is JSON
          const contentType = response.headers.get("content-type");
          if (contentType && contentType.includes("application/json")) {
            const accountData = await response.json();
            console.log("Account status from Stripe:", accountData);
            
            // Get user ID associated with this Stripe account
            const { data: userProfile } = await supabase
              .from('user_profiles')
              .select('user_id')
              .eq('stripe_connect_id', connectedAccountId)
              .single();
              
            if (!userProfile) {
              throw new Error("Could not find user profile for this Stripe account");
            }
              
            // Update user profile with status - ALWAYS update regardless of status
            // This ensures the database is in sync with Stripe
            const newStatus = (accountData.charges_enabled && accountData.payouts_enabled) 
              ? 'active' 
              : 'pending';
              
            console.log(`Updating account status to ${newStatus} for account ${connectedAccountId}`);
            
            const { error: updateError } = await supabase
              .from('user_profiles')
              .update({
                stripe_account_status: newStatus,
                updated_at: new Date().toISOString()
              })
              .eq('stripe_connect_id', connectedAccountId);
                
            if (updateError) {
              console.error("Error updating profile status:", updateError);
            } else {
              console.log("Account status updated successfully");
            }
          } else {
            console.warn("Non-JSON response from check-account-type:", await response.text());
          }
        } catch (err) {
          console.error("Error checking account status:", err);
        }
        
        // Always redirect to dashboard after a short delay
        setTimeout(() => {
          navigate("/dashboard");
        }, 2000);
      } catch (err) {
        console.error("Error in Return component:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    checkAccountStatus();
  }, [connectedAccountId, navigate]);

  return (
    <div className="container">
      <Header title="Account Setup" />
      <div className="content">
        <h2>Thank You for Connecting Your Account</h2>
        {loading ? (
          <div className="loading-state">
            <LoadingIndicator size="large" centered />
            <p>Processing your account information...</p>
          </div>
        ) : error ? (
          <div className="error-message">
            <p>There was a problem with your account setup: {error}</p>
            <button onClick={() => navigate("/dashboard")}>
              Go to Dashboard
            </button>
          </div>
        ) : (
          <div className="success-message">
            <p>Your account has been successfully connected!</p>
            <p>You'll be redirected to the dashboard in a moment.</p>
          </div>
        )}
      </div>
    </div>
  );
}